// #65 Widgets and Widget: Home's registry grid (DC-43, DC-52, DC-57). Each widget is a Card carrying data-widget (its
// registry key) and data-roles (the seats it is for); the grid draws the union for the viewer's seats, once each, in
// the fixed order alert → KPIs → Action Center → sections → health / readiness → timelines → reports, whatever order
// the widgets arrive in. A widget loading is a skeleton at its final height; an empty widget draws its own empty state.

import { Children, isValidElement, type ReactElement, type ReactNode } from "react";
import { Card, type CardProps } from "../core/Card";
import type { SeatKey } from "../labels";
import { cx } from "../lib/cx";

/** The registry's slots, in the order DS #65 fixes. */
export const WIDGET_KINDS = ["alert", "kpis", "actionCenter", "sections", "health", "timelines", "reports"] as const;
export type WidgetKind = (typeof WIDGET_KINDS)[number];

export interface WidgetProps extends Omit<CardProps, "loading" | "id"> {
  /** The registry key (data-widget), unique on the page. */
  id: string;
  kind: WidgetKind;
  /** The seats the widget is for (data-roles). */
  roles: readonly SeatKey[];
  /** Spans the grid's full width (alerts and KPI rows do by default). */
  wide?: boolean | undefined;
  /** Loading: a skeleton block at the widget's final height in px (true: three lines). */
  loading?: boolean | number | undefined;
}

export function Widget({ id, kind, roles, wide, loading, className, ...card }: WidgetProps) {
  const full = wide ?? (kind === "alert" || kind === "kpis");
  return (
    <div className={cx("mc-widget", full && "mc-widget-wide", className)} data-widget={id} data-roles={roles.join(" ")} data-kind={kind}>
      <Card {...card} loading={typeof loading === "number" ? { height: loading } : loading} />
    </div>
  );
}

export interface WidgetsProps {
  /** Widget elements, in any order. */
  children?: ReactNode;
  /** The viewer's seats: only widgets for one of them are drawn (the union for a multi-role person). Absent: all. */
  seats?: readonly SeatKey[] | undefined;
  className?: string | undefined;
}

export function Widgets({ children, seats, className }: WidgetsProps) {
  const widgets = Children.toArray(children).filter(
    (child): child is ReactElement<WidgetProps> => isValidElement(child) && child.type === Widget,
  );
  const seen = new Set<string>();
  const shown = widgets
    .filter((widget) => !seats || widget.props.roles.some((role) => seats.includes(role)))
    .filter((widget) => (seen.has(widget.props.id) ? false : (seen.add(widget.props.id), true)))
    .map((widget, index) => ({ widget, index, order: WIDGET_KINDS.indexOf(widget.props.kind) }))
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ widget }) => widget);
  return <div className={cx("mc-widgets", className)}>{shown}</div>;
}
